import { RecurrenceConverters } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';

import { IUpdateScheduleParams, UpdateScheduleRequest } from '../../backgroundJob/types/UpdateScheduleRequest';
import { Messages } from '../../codegen/Localize';
import { ContentActionNotification } from '../../commonInfra/contentActions/ContentActionNotification';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ScheduleCreateOrEditActionBody, ScheduleCreateOrEditActionBodyProps, StateForScheduleCreateOrEditActionBody } from '../../schedule/components/ScheduleCreateEditActionBody';
import { CreateScheduleRequest, ICreateScheduleParams, ICreateScheduleResult } from '../../schedule/types/CreateScheduleRequest';
import { IHourlySchedule, ISchedule, ScheduleAction, ScheduleTypes } from '../../schedule/types/ISchedule';
import { L10n } from '../L10n/L10n';
import { ScheduleErrorsService } from './ScheduleErrors';


export enum ScheduleDialogMode {
  Create,
  Edit
}

export namespace CreateOrUpdateScheduleActionService {
  export function createSchedule(): Promise<IResult> {
    return showDialog(ScheduleDialogMode.Create);
  }

  export function editSchedule(schedule: ISchedule): Promise<IResult> {
    return showDialog(ScheduleDialogMode.Edit, schedule);
  }

  export function getInitialDialogState(schedule?: ISchedule): StateForScheduleCreateOrEditActionBody {
    return {
      id: schedule ? schedule.id : undefined, // default ID is undefined
      name: schedule ? schedule.name : '', // default name is empty
      scheduledAction: schedule ? schedule.scheduledAction as ScheduleAction : ScheduleAction.Extract,
      priority: schedule ? schedule.priority : 50,
      parallel: schedule ? schedule.parallel : true,
      recurrence: RecurrenceConverters.convertLegacyScheduleToRecurrence({
        scheduleType: schedule ? schedule.scheduleType : ScheduleTypes.Hourly,
        scheduleDetails: schedule ? schedule.scheduleDetails : {
          startTime: 0,
          endTime: 0,
          recurrenceMinutes: 60,
        } as IHourlySchedule
      }),
      enabledForLinkedTasks: schedule && schedule.enabledForLinkedTasks !== undefined ? schedule.enabledForLinkedTasks : false,
    };
  }

  export function getDialogAction(mode: ScheduleDialogMode): (props: ScheduleCreateOrEditActionBodyProps, state: StateForScheduleCreateOrEditActionBody) => Promise<ICreateScheduleResult | IResult> {
    return (props: ScheduleCreateOrEditActionBodyProps, state: StateForScheduleCreateOrEditActionBody) => {
      const schedule = RecurrenceConverters.convertRecurrenceToLegacySchedule(state.recurrence);
      const params = {
        name: state.name,
        scheduleType: schedule.scheduleType,
        scheduleDetails: schedule.scheduleDetails,
        priority: state.priority,
        parallel: state.parallel,
        enabledForLinkedTasks: false,
        scheduledAction: state.scheduledAction,
        id: state.id,
      };
      const request = mode === ScheduleDialogMode.Create
        ? new CreateScheduleRequest(params)
        : new UpdateScheduleRequest(params);
      return WebHttpClientWithErrorToastInstance.sendRequest<ICreateScheduleParams | IUpdateScheduleParams, ICreateScheduleResult | IResult>(request).then(result => {
        CreateOrUpdateScheduleActionService.notifyResult(result, state.name, state.name);
        return result;
      });
    };
  }

  function showDialog(
    mode: ScheduleDialogMode,
    schedule?: ISchedule): Promise<IResult> {
    let title: string;
    let actionName: string;
    if (mode === ScheduleDialogMode.Create) {
      title = Messages.createScheduleDialogTitle();
      actionName = Messages.createScheduleDialog_create();
    } else {
      title = Messages.editScheduleDialogTitle();
      actionName = Messages.createScheduleDialog_save();
    }

    const action = getDialogAction(mode);
    const initialProps: ScheduleCreateOrEditActionBodyProps = {
      dialogMode: SelfServiceSchedulePaneType.AdminLegacySchedule,
      isNew: mode === ScheduleDialogMode.Create,
    };
    const initialState = getInitialDialogState(schedule);
    const modalInstance = openReactActionDialog({
      title: title,
      bodyComponent: ScheduleCreateOrEditActionBody,
      initialBodyProps: initialProps,
      initialDialogState: initialState,
      actionName: actionName,
      action: action,
      actionDisabled: (props, state) => state.name.length === 0,
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });
    return modalInstance.result;
  }

  export function notifyResult(result: IResult, originalScheduleName: string, newScheduleName?: string) {
    if (result.errors && result.errors.length) {

      const errorMessage = ScheduleErrorsService.errorMessageFor(result.errors[0], originalScheduleName);

      ContentActionNotification.displayFailureToast(errorMessage);

    } else {
      ContentActionNotification.displaySuccessToast(
        Messages.updateScheduleSuccess({ SCHEDULE_NAME: L10n.elideIfLong(newScheduleName || originalScheduleName)})
      );
    }
  }
}
